import React, { useMemo } from 'react';
import {
  Box,
  costFormater,
  Inscription,
} from '@common-fe/common-fe';

import { ContributionAchievementsName } from './queries/useGetContributionsHistory.query';

const nameFormatter = (name: string, isInline?: boolean) => {
  if (name === ContributionAchievementsName.INDIVIDUAL) {
    return `Individual ${isInline ? 'Max' : 'limit'}`;
  }
  if (name === ContributionAchievementsName.FAMILY) {
    return `Family ${isInline ? 'Max' : 'limit'}`;
  }

  return name;
};

interface Props {
  name: string;
  maxValue: number;
  currentValue: number;
  maxContributionValue: number;
  inlineStyle?: boolean;
  isWithSeparator?: boolean;
  maxValueDescription?: string;
}

export const ContributionLimitInfoBox: React.FC<Props> = ({
  name,
  maxValue,
  currentValue,
  maxContributionValue,
  inlineStyle,
  isWithSeparator,
  maxValueDescription,
}) => {
  const individualLimitValue = currentValue >= maxValue ? maxValue : currentValue;
  const familyLimitValue = currentValue >= maxValue ? maxValue : currentValue;
  const catchUpLimitValue = currentValue >= maxContributionValue
    ? currentValue - maxContributionValue
    : 0;

  const currentLimitValue = useMemo(() => {
    if (name === ContributionAchievementsName.INDIVIDUAL) {
      return costFormater(individualLimitValue, true);
    }
    if (name === ContributionAchievementsName.FAMILY) {
      return costFormater(familyLimitValue, true);
    }
    if (name === ContributionAchievementsName.CATCH_UP) {
      return costFormater(catchUpLimitValue, true);
    }

    return costFormater(0, true);
  }, [catchUpLimitValue, familyLimitValue, individualLimitValue, name]);

  if (inlineStyle) {
    return (
      <>
        <Box direction="row" gap="spacing2" data-testid="ContributionLimitInfoBox_wrapper">
          <Inscription weight={500} lineHeight="18px" size="12px" color="textBody">
            {nameFormatter(name, true)}:
          </Inscription>
          <Inscription 
            weight={700}
            lineHeight="18px"
            size="12px"
            color="textBody"
            data-testid={`ContributionLimitInfoBox_${currentLimitValue}-${costFormater(maxValue, true)}`}
          >
            {maxValueDescription || currentLimitValue}
          </Inscription>
        </Box>
        {isWithSeparator && (
          <Box
            width="4px"
            height="4px"
            background={{ color: 'border' }}
            round="50%"
            margin={{ horizontal: 'spacing8' }}
            alignSelf='center'
            data-testid="ContributionLimitInfoBox_separator"
          />
        )}
      </>
    );
  }

  return (
    <Box
      data-testid="ContributionLimitInfoBox_wrapper"
      pad={{ vertical: 'spacing8', horizontal: 'spacing16' }}
      border={{ color: 'border1' }}
      round="snackbarRound"
      width="100%"
    >
      <Inscription
        weight={500}
        lineHeight="18px"
        size="12px"
        color="textDisabled"
        margin={{ bottom: 'spacing2' }}
      >
        {nameFormatter(name)}
      </Inscription>
      <Inscription lineHeight="20px" data-testid={`ContributionLimitInfoBox_${currentLimitValue}-${costFormater(maxValue, true)}`}>
        {maxValueDescription || currentLimitValue} / {costFormater(maxValue, true)}
      </Inscription>
    </Box>
  );
};
